import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { ProtectedRoutes } from './ProtectedRoutes';
import { Layout } from 'components/shell/Layout';
import { MovProvider } from 'contexts/movementsContext/MovProvider';
import { UsersState } from 'contexts/usersContext/UsersState';
import { RolesState } from 'contexts/rolesContext/RolesState';
import { AppState } from 'contexts/global/AppState';
import { PresentationSheetState } from 'contexts/presentationSheetContext/PresentationSheetState';
import { BalanceState } from 'contexts/balanceContext/BalanceState';
import { DeregistrationState } from 'contexts/deregistrationContext/DeregistrationState';

const Movements = lazy(() => import('pages/movement/Movements').then((module) => ({ default: module.Movements })));
const Home = lazy(() => import('../pages').then((module) => ({ default: module.Home })));
const RegistrationPage = lazy(() => import('../pages').then((module) => ({ default: module.RegistrationPage })));
const UsersPage = lazy(() => import('../pages').then((module) => ({ default: module.UsersPage })));
const PresentationSheetPage = lazy(() =>
	import('../pages').then((module) => ({ default: module.PresentationSheetPage }))
);
const PresentationSheetAuditPage = lazy(() =>
	import('../pages').then((module) => ({ default: module.PresentationSheetAuditPage }))
);
const BalancePage = lazy(() => import('../pages').then((module) => ({ default: module.BalancePage })));
const ParametersPage = lazy(() => import('../pages').then((module) => ({ default: module.ParametersPage })));
const DeregistrationPage = lazy(() => import('../pages').then((module) => ({ default: module.DeregistrationPage })));
const MovementEdit = lazy(() => import('pages/movement').then((module) => ({ default: module.MovementEdit })));
const BalanceSummaryPage = lazy(() =>
	import('pages/balanceSummary/BalanceSummary').then((module) => ({ default: module.BalanceSummaryPage }))
);
const PresentationSheetManagement = lazy(() =>
	import('pages/presentationSheet/PresentationSheetManagement').then((module) => ({
		default: module.PresentationSheetManagement
	}))
);
const PresentationSheetGeneration = lazy(() =>
	import('pages/presentationSheet/PresentationSheetGeneration').then((module) => ({
		default: module.PresentationSheetGeneration
	}))
);
// Ruta interna de prueba del design system: solo existe si el build se hizo con VITE_DS_PLAYGROUND=true.
const DS_PLAYGROUND = import.meta.env.VITE_DS_PLAYGROUND === 'true';
const DesignSystemPlayground = lazy(() =>
	import('pages/designSystem/DesignSystemPlayground').then((module) => ({ default: module.DesignSystemPlayground }))
);
const UnauthorizedPage = lazy(() => import('pages/UnauthorizedPage'));
import { LocalSpinner } from 'components/globals/spinner/LocalSpinner';

export const Views = () => {
	return (
		<AppState>
			<MovProvider>
				<Routes>
					<Route
						path="unauthorized"
						element={
							<Suspense fallback={<LocalSpinner />}>
								<UnauthorizedPage />
							</Suspense>
						}
					/>
					<Route element={<ProtectedRoutes />}>
						<Route element={<Layout />}>
							<Route
								path="*"
								element={
									<Navigate
										to="home"
										replace
									/>
								}
							/>
							<Route
								path="home"
								element={
									<Suspense fallback={<LocalSpinner />}>
										<Home />
									</Suspense>
								}
							/>

							{DS_PLAYGROUND && (
								<Route
									path="ds-playground"
									element={
										<Suspense fallback={<LocalSpinner />}>
											<DesignSystemPlayground />
										</Suspense>
									}
								/>
							)}

							<Route
								path="alta"
								element={
									<Suspense fallback={<LocalSpinner />}>
										<RegistrationPage />
									</Suspense>
								}
							/>

							<Route
								path="alta/editar/:registrationId"
								element={
									<Suspense fallback={<LocalSpinner />}>
										<RegistrationPage />
									</Suspense>
								}
							/>

							<Route
								path="movimientos/:movement_type"
								element={
									<DeregistrationState>
										<Suspense fallback={<LocalSpinner />}>
											<Movements />
										</Suspense>
									</DeregistrationState>
								}
							/>

							<Route
								path="movimientos/:movement_type/editar"
								element={
									<Suspense fallback={<LocalSpinner />}>
										<MovementEdit />
									</Suspense>
								}
							/>
							<Route
								path="configuracion/usuarios"
								element={
									<UsersState>
										<RolesState>
											<Suspense fallback={<LocalSpinner />}>
												<UsersPage />
											</Suspense>
										</RolesState>
									</UsersState>
								}
							/>
							<Route
								path="configuracion/parametros"
								element={
									<Suspense fallback={<LocalSpinner />}>
										<ParametersPage />
									</Suspense>
								}
							/>
							<Route
								path="planilla-mensual"
								element={
									<PresentationSheetState>
										<Suspense fallback={<LocalSpinner />}>
											<PresentationSheetPage />
										</Suspense>
									</PresentationSheetState>
								}
							/>

							<Route
								path="planilla-mensual-auditoria"
								element={
									<PresentationSheetState>
										<Suspense fallback={<LocalSpinner />}>
											<PresentationSheetAuditPage />
										</Suspense>
									</PresentationSheetState>
								}
							/>

							<Route
								path="planilla-mensual-gestionar"
								element={
									<PresentationSheetState>
										<Suspense fallback={<LocalSpinner />}>
											<PresentationSheetManagement />
										</Suspense>
									</PresentationSheetState>
								}
							/>

							<Route
								path="planilla-mensual-generar"
								element={
									<PresentationSheetState>
										<Suspense fallback={<LocalSpinner />}>
											<PresentationSheetGeneration />
										</Suspense>
									</PresentationSheetState>
								}
							/>

							<Route
								path="balance/cierre-ejercicio"
								element={
									<BalanceState>
										<Suspense fallback={<LocalSpinner />}>
											<BalancePage />
										</Suspense>
									</BalanceState>
								}
							/>

							<Route
								path="balance/resumen"
								element={
									<BalanceState>
										<Suspense fallback={<LocalSpinner />}>
											<BalanceSummaryPage />
										</Suspense>
									</BalanceState>
								}
							/>

							<Route
								path="baja"
								element={
									<Suspense fallback={<LocalSpinner />}>
										<DeregistrationPage />
									</Suspense>
								}
							/>

							<Route
								path="baja/editar/:registrationId"
								element={
									<Suspense fallback={<LocalSpinner />}>
										<DeregistrationPage />
									</Suspense>
								}
							/>
						</Route>
					</Route>
				</Routes>
			</MovProvider>
		</AppState>
	);
};
